// Owned Firebase adapter boundary.
//
// The runtime never relies on the browser-level globals exposed by the
// Firebase SDK or by the legacy application namespace. Instead, every
// feature that needs authentication or persistence imports its helpers
// from this module. The adapter resolves the underlying implementation
// lazily through a dynamic import so the critical entry stays free of
// Firebase code.
//
// Bootstrap MUST NOT call signIn(). Guest access is implicit; explicit
// sign-in happens lazily on the first action that requires authentication.

const ADAPTER_PATHS = [
  '../../../modules/firebase-runtime-v2.js',
  '../../../modules/firebase-local.js',
  '../../../modules/firebase-init.js',
];

let adapterPromise = null;
let persistencePromise = null;

async function resolveAdapter() {
  if (!adapterPromise) {
    adapterPromise = (async () => {
      for (const path of ADAPTER_PATHS) {
        try {
          const mod = await import(/* @vite-ignore */ path);
          if (mod) return mod;
        } catch (error) {
          console.warn(`[sctmgUtils] firebase adapter path unavailable: ${path}`, error);
        }
      }
      return null;
    })();
  }
  return adapterPromise;
}

async function resolvePersistence() {
  if (!persistencePromise) {
    persistencePromise = import('../../../modules/army-persistence-store.js');
  }
  return persistencePromise;
}

async function authenticatedUser() {
  const persistence = await resolvePersistence();
  const user = await persistence.authenticatedUser();
  if (!user || user.isAnonymous || !user.uid) throw new Error('AUTH_REQUIRED');
  return user;
}

export async function getCurrentUser() {
  const adapter = await resolveAdapter();
  if (!adapter) return null;
  await adapter.ensureFirebaseAuth?.();
  if (typeof adapter.getCurrentUser === 'function') return adapter.getCurrentUser();
  if (typeof adapter.getAuthState === 'function') return adapter.getAuthState();
  const auth = adapter.auth || adapter.getAuth?.();
  if (auth && typeof auth.currentUser !== 'undefined') return auth.currentUser || null;
  return null;
}

export async function signIn() {
  const adapter = await resolveAdapter();
  if (!adapter) throw new Error('[sctmgUtils] firebase adapter unavailable for signIn');
  if (typeof adapter.signIn === 'function') return adapter.signIn();
  throw new Error('[sctmgUtils] signIn is not implemented by the current adapter');
}

// Google sign-in lives behind the adapter boundary so the critical
// entry never imports the Firebase SDK. The runtime resolves this
// lazily when the user clicks the Google login button; bootstrap does
// not auto-authenticate.
export async function signInWithGoogle(target) {
  const adapter = await resolveAdapter();
  if (!adapter) {
    throw new Error('[sctmgUtils] firebase adapter unavailable for signInWithGoogle');
  }
  if (typeof adapter.ensureFirebaseAuth === 'function') {
    await adapter.ensureFirebaseAuth({ forceCloud: true });
  }
  if (typeof adapter.signInWithGoogle === 'function') {
    return adapter.signInWithGoogle(target);
  }
  if (adapter.cloudAuth && adapter.cloudEnabled) {
    return adapter.signInWithGoogle(target);
  }
  throw new Error('[sctmgUtils] signInWithGoogle is not implemented by the current adapter');
}

export async function signOut() {
  const adapter = await resolveAdapter();
  if (!adapter) throw new Error('[sctmgUtils] firebase adapter unavailable for signOut');
  if (typeof adapter.signOut === 'function') return adapter.signOut(adapter.auth);
  throw new Error('[sctmgUtils] signOut is not implemented by the current adapter');
}

export async function consumeGoogleRedirect() {
  const adapter = await resolveAdapter();
  if (!adapter || typeof adapter.consumeGoogleRedirect !== 'function') return null;
  await adapter.ensureFirebaseAuth?.();
  return adapter.consumeGoogleRedirect();
}

export async function onAuthStateChanged(callback) {
  const adapter = await resolveAdapter();
  if (!adapter) return () => {};
  await adapter.ensureFirebaseAuth?.();
  if (typeof adapter.onAuthStateChanged !== 'function') return () => {};
  return adapter.onAuthStateChanged(adapter.auth, callback);
}

export async function save(coll, id, payload) {
  const adapter = await resolveAdapter();
  if (!adapter) throw new Error('[sctmgUtils] firebase adapter unavailable for save');
  if (typeof adapter.save === 'function') return adapter.save(coll, id, payload);
  if (typeof adapter.setDoc === 'function' && typeof adapter.doc === 'function') {
    const reference = adapter.doc(adapter.db, coll, id);
    return adapter.setDoc(reference, payload);
  }
  throw new Error('[sctmgUtils] save is not implemented by the current adapter');
}

// ---------------------------------------------------------------------------
// Army list persistence (Increment 1 — adapter and model).
//
// The adapter is the ownership boundary for private army lists. The
// authenticated account is the authority for `ownerId`; callers MUST NOT
// pass a user-supplied UID. Guest/anonymous users (Firebase `isAnonymous`)
// cannot persist private armies — listArmies/saveArmy throw `AUTH_REQUIRED`
// so the UI can surface a sign-in prompt instead of writing silently.
//
// Path helper exposes the strict private path `users/{uid}/armies/{armyId}`.
// Serializer delegates to modules/army-persistence.js (buildArmyRecord) so
// the document shape, `formatVersion` and selections filter stay in one
// place. Legacy modules/army-persistence-store.js remains the runtime
// implementation for cloud/local backends; this adapter only adds the
// async, uid-from-auth boundary that the new contract requires.
// ---------------------------------------------------------------------------

export async function privateArmyPath(uid, armyId) {
  if (!uid || !armyId) throw new Error('uid and armyId are required');
  return `users/${uid}/armies/${armyId}`;
}

export async function listArmies() {
  const persistence = await resolvePersistence();
  const user = await authenticatedUser();
  return persistence.listArmies(user);
}

export async function saveArmy(input, { isNew = false } = {}) {
  const persistence = await resolvePersistence();
  const user = await authenticatedUser();
  return persistence.saveArmy(user, input, { isNew });
}

export async function deleteArmy(armyId) {
  const persistence = await resolvePersistence();
  const user = await authenticatedUser();
  return persistence.deleteArmy(user, armyId);
}

export async function duplicateArmy(source) {
  const persistence = await resolvePersistence();
  const user = await authenticatedUser();
  return persistence.duplicateArmy(user, source);
}

export async function createShare(source) {
  const persistence = await resolvePersistence();
  const user = await authenticatedUser();
  return persistence.createShare(user, source);
}

export async function loadShare(shareId) {
  const persistence = await resolvePersistence();
  return persistence.loadShare(shareId);
}

export async function importShare(shareId, name) {
  const persistence = await resolvePersistence();
  const user = await authenticatedUser();
  return persistence.importShare(user, shareId, name);
}

export async function revokeShare(shareId) {
  const persistence = await resolvePersistence();
  const user = await authenticatedUser();
  return persistence.revokeShare(user, shareId);
}
